import { useCallback, useEffect } from 'react';
import type { MutationCreateMeasuringPointCommentArgs } from '@/graphql/types';
import LoadingSpinner from '../../../../components/loadingSpinner/LoadingSpinner';
import { RoundedButton } from '../../../../components/form/Button';
import TextArea from '../../../../components/form/TextArea';
import {
    NOTIFICATION_VARIANT,
    NotificationsContainer,
    useNotifications,
} from '../../../../components/notifications/Notifications';

import { MAX_COMMENT_LENGTH } from './CommentInput';
import { useCreateComment } from '../hooks';

interface CommentCreateViewProps {
    partialComment: MutationCreateMeasuringPointCommentArgs;
    onSuccess: () => void;
    onClose: () => void;
}

function CommentCreateView({
    partialComment,
    onSuccess,
    onClose,
}: CommentCreateViewProps): JSX.Element {
    const onCreateComplete = useCallback(() => {
        // Calling `onSuccess` will remove the selection.
        onSuccess();
        onClose();
    }, [onSuccess, onClose]);

    const {
        isLoading: loading,
        error,
        newCommentText,
        setNewCommentText,
        createComment,
    } = useCreateComment(onCreateComplete);
    const { addNotification, clearNotifications } = useNotifications();

    useEffect(() => {
        clearNotifications();
        if (error) {
            addNotification({
                message: gettext('GENERIC_SERVER_ERROR'),
                variant: NOTIFICATION_VARIANT.ERROR,
            });
        }
    }, [error, addNotification, clearNotifications]);

    return (
        <>
            <p className="text-center">{gettext('WRITE_COMMENT')}</p>
            <TextArea
                maxLength={MAX_COMMENT_LENGTH}
                value={newCommentText}
                onValueChange={setNewCommentText}
                disabled={loading}
            />
            {error && <NotificationsContainer notificationProps={{ textSize: 'text-xs' }} />}
            <div className="mt-2 space-x-3">
                <RoundedButton
                    type="button"
                    onClick={() => {
                        void createComment(partialComment);
                    }}
                    variant="big"
                    color="primary"
                    disabled={loading}
                    flex
                >
                    {loading ? <LoadingSpinner /> : gettext('ADD')}
                </RoundedButton>
                <RoundedButton type="button" onClick={onClose} variant="big" color="secondary" flex>
                    {gettext('CANCEL')}
                </RoundedButton>
            </div>
        </>
    );
}

export default CommentCreateView;
